import BGD2026 from "@assets/budget/B2026-logo.svg";
import GELogo from "@assets/ge/GE2025-logo.png";
import NDP2026 from "@assets/ndp/sg61.svg";
import { NDP_2026_TAG, NDR_2026_TAGNAME } from "@config/KeywordConstants";
import Divider from "@elements/Divider/Divider";
import Image from "@elements/ResponsiveImage/Image";
import Heading from "@elements/Typography/Heading";
import type { ReactElement } from "react";
import { twMerge } from "tailwind-merge";

const SPECIAL_HEADER_CONFIG = {
  "பட்ஜெட் 2026": {
    type: "budget-2026",
    image: BGD2026,
    imageSize: { width: 141, height: 48 },
  },
  "தமிழ்மொழி விழா 2026": {
    type: "tamil-murasu-festival-2026",
    image: undefined,
    imageSize: undefined,
  },
  "சட்டமன்றத் தேர்தல் 2026": {
    type: "state-election-2026",
    image: undefined,
    imageSize: undefined,
  },
  "பொதுத் தேர்தல் 2025": {
    type: "election-2025",
    image: GELogo,
    imageSize: { width: 48, height: 48 },
  },
  "தீபாவளி 2025": {
    type: "deepavali-2025",
    image: undefined,
    imageSize: undefined,
  },
  தீபாவளி: {
    type: "deepavali",
    image: undefined,
    imageSize: undefined,
  },
  [NDR_2026_TAGNAME]: {
    type: NDP_2026_TAG,
    image: NDP2026,
    imageSize: { width: 40, height: 40 },
  },
} as const;

export type SpecialHeaderProps = {
  title: string;
};

const CustomHeading = ({
  title,
  className,
}: {
  title: string;
  className?: string;
}) => {
  return (
    <Heading
      Element="h1"
      className={twMerge(
        "font-bold font-primary text-2.5md leading-960 lg:text-1xl",
        className,
      )}
    >
      {title}
    </Heading>
  );
};

export function SpecialHeader({ title }: SpecialHeaderProps): ReactElement {
  if (!(title in SPECIAL_HEADER_CONFIG)) {
    return <CustomHeading title={title} />;
  }

  const config =
    SPECIAL_HEADER_CONFIG[title as keyof typeof SPECIAL_HEADER_CONFIG];

  const { type, image, imageSize } = config;

  return (
    <>
      {type === "budget-2026" && (
        <>
          <div className="flex items-center justify-between pb-2.5">
            <CustomHeading title={title} className="" />
            <Image
              srcWidth={imageSize.width}
              srcHeight={imageSize.height}
              src={image}
              objectFit="contain"
              alt={title}
            />
          </div>
          <Divider className="border-b border-gray-2200 mb-5" />
        </>
      )}

      {type === "election-2025" && (
        <div>
          <Image
            srcWidth={imageSize.width}
            srcHeight={imageSize.height}
            src={image}
            objectFit="contain"
            alt={title}
            className="lg:w-12 w-12 absolute"
          />
          <CustomHeading
            title={title}
            className="text-pink-200 mb-5 pl-12 ml-2.5"
          />
          <Divider className="border-b border-gray-2200 mb-5" />
        </div>
      )}

      {type === NDP_2026_TAG && (
        <div className="flex items-center justify-between min-h-[40px]">
          <Image
            srcWidth={imageSize.width}
            srcHeight={imageSize.height}
            src={image}
            objectFit="contain"
            alt={title}
            className="lg:w-10 w-10 absolute"
          />
          <CustomHeading
            title={title}
            className="text-sm md:text-lg text-red-450 pl-12 ml-2.5"
          />
        </div>
      )}

      {(type === "deepavali-2025" || type === "deepavali") && (
        <div>
          <CustomHeading title={title} className="text-purple-800 mb-5" />
        </div>
      )}

      {(type === "tamil-murasu-festival-2026" ||
        type === "state-election-2026") && (
        <div>
          <CustomHeading title={title} className="mb-5" />
        </div>
      )}
    </>
  );
}
